import React from 'react';
import { LocalizedMessage, LocalizedStandardDateNoSeconds } from 'intl/LocalizedComponents';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _size from 'lodash/size';
import _toString from 'lodash/toString';
import PropTypes from 'prop-types';
// Core components
import Divider from 'components/core/Divider';
import List from 'components/core/List';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mParking from 'models/cpm/parking';
import mPlanning from 'models/cpm/planning';
// Custom
import { cpmParkingDataShape, cpmPlanningDataShape } from 'constants/PropTypesShapes';
import { dateGenerator } from 'utils/DateUtils';
import InformationItem from 'components/items/InformationItem';
import {
  SpacesQuantityItem, StatusItem, DateBeginItem, DateEndItem, PlanningItem, ParkingTypeItem,
} from 'components/items/InformationItems';
import Callout from 'components/misc/Callout';
import ParkingCategoryAltItem from 'cpmCarparks/assets/parkingCategories/ParkingCategoryAltItem';

const ParkingTooltipContent = ({
  parking, timezone, parkingPlanning, allotmentPlanning,
  withHeaderMessage, withParkingCategory, withStatus,
}) => {
  const {
    begin, end, spacesQuantity, keypadCodes, vehicles,
  } = parking;

  // Status
  const { labelId: statusLabelId, icon: statusIcon, color: statusColor } = mParking.status(parking);

  // Keypad codes
  const keypadCodesInfo = _size(keypadCodes) > 1 ? `(+${_size(keypadCodes) - 1})` : '';

  // License plates
  const licensePlates = _map(vehicles, 'licensePlate');
  const licensePlatesInfo = _size(licensePlates) > 1 ? `(+${_size(licensePlates) - 1})` : '';

  // For header message
  let headerMessage;
  if (withHeaderMessage) {
    const hasPlanning = !!allotmentPlanning || !!parkingPlanning;
    const severity = hasPlanning ? 'warning' : 'success';
    headerMessage = {
      severity,
      content: <LocalizedMessage id="info.parkingStatusDesc" values={{ status: mParking.status(parking).value }} />,
    };
  }

  // Permissions
  const { pCpm: { parkingCategoryRead } } = permissions;

  return (
    <List disablePadding>
      {headerMessage && (
        <div className="py-1">
          <Callout withInfoIcon={false} severity={headerMessage.severity}>
            {headerMessage.content}
          </Callout>
        </div>
      )}
      {withParkingCategory && (
        <OnlyWith
          permissions={{
            needed: parkingCategoryRead,
            permissionsOrContext: 'carparkPermissions',
          }}
        >
          <React.Fragment>
            <ParkingCategoryAltItem light parkingCategoryId={mParking.getParkingCategoryId(parking)} />
            <Divider variant="middle" />
          </React.Fragment>
        </OnlyWith>
      )}
      <SpacesQuantityItem
        light
        value={<LocalizedMessage id="info.spacesQuantity" values={{ quantity: _toString(spacesQuantity) }} />}
      />
      {begin && (
        <React.Fragment>
          <Divider variant="middle" />
          <DateBeginItem
            light
            value={<LocalizedStandardDateNoSeconds value={dateGenerator.dateFromTimezone(begin, timezone)} />}
          />
        </React.Fragment>
      )}
      {end && (
        <React.Fragment>
          <Divider variant="middle" />
          <DateEndItem
            light
            value={<LocalizedStandardDateNoSeconds value={dateGenerator.dateFromTimezone(end, timezone)} />}
          />
        </React.Fragment>
      )}
      {/* Show information for assigned space */}
      {mParking.isAssigned(parking) && (
        <React.Fragment>
          <Divider variant="middle" />
          <ParkingTypeItem light value={<LocalizedMessage id={mParking.type(parking).labelId} />} />
        </React.Fragment>
      )}
      {!_isEmpty(keypadCodes) && (
        <React.Fragment>
          <Divider variant="middle" />
          <InformationItem light icon="cAwTypeKCode" value={`${keypadCodes[0]} ${keypadCodesInfo}`} />
        </React.Fragment>
      )}
      {!_isEmpty(licensePlates) && (
        <React.Fragment>
          <Divider variant="middle" />
          <InformationItem light icon="cAwTypeLPlate" value={`${licensePlates[0]} ${licensePlatesInfo}`} />
        </React.Fragment>
      )}
      {withStatus && (
        <React.Fragment>
          <Divider variant="middle" />
          <StatusItem light icon={statusIcon} iconColor={statusColor} value={<LocalizedMessage id={statusLabelId} />} />
        </React.Fragment>
      )}
      {allotmentPlanning && (
        <React.Fragment>
          <Divider variant="middle" />
          <PlanningItem light value={mPlanning.title(allotmentPlanning)} />
        </React.Fragment>
      )}
      {parkingPlanning && (
        <React.Fragment>
          <Divider variant="middle" />
          <PlanningItem light value={mPlanning.title(parkingPlanning)} />
        </React.Fragment>
      )}
    </List>
  );
};

ParkingTooltipContent.propTypes = {
  allotmentPlanning: cpmPlanningDataShape,
  parking: cpmParkingDataShape.isRequired,
  timezone: PropTypes.string.isRequired,
  parkingPlanning: cpmPlanningDataShape,
  withHeaderMessage: PropTypes.bool,
  withParkingCategory: PropTypes.bool,
  withStatus: PropTypes.bool,
};

ParkingTooltipContent.defaultProps = {
  allotmentPlanning: null,
  parkingPlanning: null,
  withHeaderMessage: false,
  withParkingCategory: false,
  withStatus: false,
};

module.exports = ParkingTooltipContent;



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/parkings/ParkingTooltipContent.js